import React, { useState } from 'react';
import { AlertModal } from 'components';
import styles from './AddPromptModal.module.scss';
import { Select } from 'common/components';

// Type definitions
type Tenant = {
    id: string;
    name: string;
};

type PromptData = {
    title: string;
    description: string;
    prompt: string;
    language: string;
    tenantId: string;
};

type AlertType = 'success' | 'error' | 'warning' | 'info';

type AddPromptModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSave: (promptData: PromptData) => void;
    tenants?: Tenant[];
};

// Type for AlertModal props (since it's not TypeScript yet)
type AlertModalProps = {
    isOpen: boolean;
    onClose: () => void;
    message: string;
    type?: AlertType;
};

const AddPromptModal: React.FC<AddPromptModalProps> = ({ isOpen, onClose, onSave, tenants = [] }) => {
    const [promptName, setPromptName] = useState<string>('');
    const [tenant, setTenant] = useState<string>('');
    const [language, setLanguage] = useState<string>('english');
    const [description, setDescription] = useState<string>('');
    const [promptText, setPromptText] = useState<string>('');

    // State for AlertModal
    const [isAlertOpen, setIsAlertOpen] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>('');
    const [alertType, setAlertType] = useState<AlertType>('warning');

    // Show alert helper function
    const showAlert = (message: string, type: AlertType = 'warning'): void => {
        setAlertMessage(message);
        setAlertType(type);
        setIsAlertOpen(true);
    };

    // Close alert helper function
    const closeAlert = (): void => {
        setIsAlertOpen(false);
    };

    const handleSave = (): void => {
        if (!promptName.trim()) {
            showAlert('Please enter a prompt name', 'warning');
            return;
        }

        const promptData: PromptData = {
            title: promptName,
            description: description,
            prompt: promptText,
            language: language,
            tenantId: tenant, // Rename tenant to tenantId
        };

        onSave(promptData);
        resetForm();
    };

    const resetForm = (): void => {
        setPromptName('');
        setTenant('');
        setLanguage('english');
        setDescription('');
        setPromptText('');
    };

    if (!isOpen) return null;

    return (
        <>
            <div className={styles.modalOverlay}>
                <div className={styles.modalContainer}>
                    <div className={styles.modalHeader}>
                        <h2>
                            <span className={styles.sparkle}>✨</span> Add New Prompt
                        </h2>
                        <button className={styles.closeButton} onClick={onClose}>
                            ×
                        </button>
                    </div>

                    <div className={styles.formContentWrapper}>
                        <div className={styles.formField}>
                            <label>Prompt Name</label>
                            <input
                                type='text'
                                value={promptName}
                                onChange={(e) => setPromptName(e.target.value)}
                                placeholder='Enter prompt name'
                                className={styles.formInput}
                            />
                        </div>

                        <div className={styles.formField}>
                            <label>Tenant</label>
                            <div className={styles.selectContainer}>
                                <Select
                                    value={tenant}
                                    onChange={(e) => setTenant(e.target.value)}
                                    options={[
                                        { value: '', label: 'Select tenant' },
                                        ...tenants.map((tenant) => ({
                                            value: tenant.id,
                                            label: tenant.name,
                                        })),
                                    ]}
                                />
                            </div>
                        </div>

                        <div className={styles.formField}>
                            <label>Language</label>
                            <div className={styles.modalLanguageSelector}>
                                <div className={styles.modalLanguageChips}>
                                    <button
                                        className={`${styles.modalLanguageChip} ${language === 'english' ? styles.active : ''}`}
                                        onClick={() => setLanguage('english')}
                                        type='button'
                                    >
                                        English
                                    </button>
                                    <button
                                        className={`${styles.modalLanguageChip} ${language === 'spanish' ? styles.active : ''}`}
                                        onClick={() => setLanguage('spanish')}
                                        type='button'
                                    >
                                        Spanish
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className={styles.formField}>
                            <label>Description</label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder='Enter prompt description'
                                className={styles.formTextarea}
                                rows={3}
                            />
                        </div>

                        <div className={styles.formField}>
                            <label>Prompt</label>
                            <textarea
                                value={promptText}
                                onChange={(e) => setPromptText(e.target.value)}
                                placeholder='Enter your AI prompt here...'
                                className={`${styles.formTextarea} ${styles.promptTextarea}`}
                                rows={5}
                            />
                        </div>
                    </div>

                    <div className={styles.modalFooter}>
                        <button className={styles.savePromptBtn} onClick={handleSave}>
                            Save Prompt
                        </button>
                    </div>
                </div>
            </div>

            {/* AlertModal Component */}
            {/* @ts-ignore - AlertModal is not TypeScript yet */}
            <AlertModal isOpen={isAlertOpen} onClose={closeAlert} message={alertMessage} type={alertType} />
        </>
    );
};

export default AddPromptModal;
